import React, { useEffect } from "react";

import zIndex from "../utils/zIndex";

export enum TooltipPosition {
  top = "top",
  right = "right",
  bottom = "bottom",
  left = "left",
}

export enum TooltipAlignment {
  start = "start",
  center = "center",
  end = "end",
}

export interface TooltipProps {
  position?: TooltipPosition | `${TooltipPosition}`;
  alignment?: TooltipAlignment | `${TooltipAlignment}`;

  /** Force the position even if it means overlapping the edges of the screen. Don't use this */
  forcePosition?: boolean;

  /** Content */
  content?: React.ReactNode;
  light?: boolean;
}

const HANDLE_SIZE = 8;
const HANDLE_HORIZONTAL_OFFSET = 12;
const MARGIN = 4;

/**
 * Calculates position of tooltip based on position and alignment of tooltip and child.
 * @param position
 * @param alignment
 * @param child
 * @param tooltip
 * @returns
 */
const calculatePosition = (position: TooltipPosition | `${TooltipPosition}`, alignment: TooltipAlignment | `${TooltipAlignment}`, child: DOMRect, tooltip: DOMRect) => {
  let top = 0;
  let left = 0;

  // For alignment
  let leftAdjustment = 0;
  let topAdjustment = 0;

  switch (position) {
    case TooltipPosition.top:
      top = child.top - tooltip.height;
      left = child.left + child.width / 2 - tooltip.width / 2;
      break;
    case TooltipPosition.right:
      top = child.top + child.height / 2 - tooltip.height / 2;
      left = child.right;
      break;
    case TooltipPosition.bottom:
      top = child.bottom;
      left = child.left + child.width / 2 - tooltip.width / 2;
      break;
    case TooltipPosition.left:
      top = child.top + child.height / 2 - tooltip.height / 2;
      left = child.left - tooltip.width;
      break;
    default:
      break;
  }

  if (position === TooltipPosition.left || position === TooltipPosition.right) {
    switch (alignment) {
      case TooltipAlignment.start:
        // Make top of tooltip align with top of child, only if it wouldn't be higher than before
        topAdjustment = Math.min((tooltip.height - child.height) / 2, 0);
        break;
      case TooltipAlignment.center:
        // Make center of tooltip align with center of child
        topAdjustment = 0;
        break;
      case TooltipAlignment.end:
        // Make bottom of tooltip align with bottom of child, only if it wouldn't be lower than before
        topAdjustment = Math.max(-(tooltip.height - child.height) / 2, 0);
        break;
      default:
        break;
    }
  } else {
    switch (alignment) {
      case TooltipAlignment.start:
        // Make right of tooltip align with middle of child
        leftAdjustment = -tooltip.width;
        break;
      case TooltipAlignment.center:
        // Make center of tooltip align with center of child
        leftAdjustment = 0;
        break;
      case TooltipAlignment.end:
        // Make left of tooltip align with middle of child
        leftAdjustment = tooltip.width;
        break;
      default:
        break;
    }
  }

  // Adjust position for handle
  switch (position) {
    case TooltipPosition.top:
      top -= HANDLE_SIZE + MARGIN;
      break;
    case TooltipPosition.right:
      left += HANDLE_SIZE + MARGIN;
      break;
    case TooltipPosition.bottom:
      top += HANDLE_SIZE + MARGIN;
      break;
    case TooltipPosition.left:
      left -= HANDLE_SIZE + MARGIN;
      break;
    default:
      break;
  }

  // When top/bottom and alignment is start/end, adjust left so that the tooltip is moved.
  if ((position === TooltipPosition.top || position === TooltipPosition.bottom)) {
    if (alignment === TooltipAlignment.start) {
      const childCenter = child.left + child.width / 2;
      const tooltipRight = (left + leftAdjustment) + tooltip.width;
      const handleRight = tooltipRight - HANDLE_SIZE - HANDLE_HORIZONTAL_OFFSET;
      const correctedTooltipLeft = handleRight - childCenter;
      leftAdjustment -= correctedTooltipLeft;
    } else if (alignment === TooltipAlignment.end) {
      const childCenter = child.left + child.width / 2;
      const tooltipLeft = left + leftAdjustment;
      const handleLeft = tooltipLeft + HANDLE_SIZE + HANDLE_HORIZONTAL_OFFSET;
      const correctedTooltipRight = handleLeft - childCenter;
      leftAdjustment -= correctedTooltipRight;
    }
  }

  return {
    top: top + topAdjustment,
    left: left + leftAdjustment,
  };
};

const calculateHandlePosition = (position: TooltipPosition | `${TooltipPosition}`, alignment: TooltipAlignment | `${TooltipAlignment}`) => {
  let left = "";
  let top = "";

  switch (position) {
    case TooltipPosition.top:
      top = "100%";
      left = "calc(50% - 8px)";
      break;
    case TooltipPosition.right:
      top = "calc(50% - 8px)";
      left = `-${HANDLE_SIZE}px`;
      break;
    case TooltipPosition.bottom:
      top = `-${HANDLE_SIZE}px`;
      left = "calc(50% - 8px)";
      break;
    case TooltipPosition.left:
      top = "calc(50% - 8px)";
      left = "100%";
      break;
    default:
      break;
  }

  if (position === TooltipPosition.top || position === TooltipPosition.bottom) {
    switch (alignment) {
      case TooltipAlignment.start:
        // Width is twice size so must be multiplied by 2
        left = `calc(100% - ${HANDLE_SIZE * 2}px - ${HANDLE_HORIZONTAL_OFFSET}px)`;
        break;
      case TooltipAlignment.center:
        break;
      case TooltipAlignment.end:
        left = `${HANDLE_HORIZONTAL_OFFSET}px`;
        break;
      default:
        break;
    }
  }

  return {
    top,
    left,
  };
};

const calculateHandleShape = (position: TooltipPosition | `${TooltipPosition}`, light: boolean) => {
  switch (position) {
    case TooltipPosition.top:
      return {
        borderTop: `${HANDLE_SIZE}px solid`,
        borderRight: `${HANDLE_SIZE}px solid`,
        borderBottom: "none",
        borderLeft: `${HANDLE_SIZE}px solid`,
        borderColor: `border-transparent ${light ? "border-t-bg-subtle" : "border-t-bg-inverted"}`,
      };
    case TooltipPosition.right:
      return {
        borderTop: `${HANDLE_SIZE}px solid`,
        borderRight: `${HANDLE_SIZE}px solid`,
        borderBottom: `${HANDLE_SIZE}px solid`,
        borderLeft: "none",
        borderColor: `border-transparent ${light ? "border-r-bg-subtle" : "border-r-bg-inverted"}`,
      };
    case TooltipPosition.bottom:
      return {
        borderTop: "none",
        borderRight: `${HANDLE_SIZE}px solid`,
        borderBottom: `${HANDLE_SIZE}px solid`,
        borderLeft: `${HANDLE_SIZE}px solid`,
        borderColor: `border-transparent ${light ? "border-b-bg-subtle" : "border-b-bg-inverted"}`,
      };
    case TooltipPosition.left:
      return {
        borderTop: `${HANDLE_SIZE}px solid`,
        borderRight: "none",
        borderBottom: `${HANDLE_SIZE}px solid`,
        borderLeft: `${HANDLE_SIZE}px solid`,
        borderColor: `border-transparent ${light ? "border-l-bg-subtle" : "border-l-bg-inverted"}`,
      };
    default:
      return {};
  }
};

const Tooltip: React.FC<React.PropsWithChildren<TooltipProps>> = ({
  position = TooltipPosition.top,
  alignment = TooltipAlignment.center,
  forcePosition = false,
  light = false,
  content,
  children,
}) => {
  const childrenRef = React.useRef<HTMLElement[]>([]);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const handleRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseEnter = () => {
      tooltipRef.current.style.display = "block";

      let correctedPosition = position;
      let correctedAlignment = alignment;

      // Get the child's position
      const childRect = childrenRef.current[0].getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      let coords = calculatePosition(position, alignment, childRect, tooltipRect);
      let handleCoords = calculateHandlePosition(position, alignment);

      // Adjust the position if it's outside the screen
      if (!forcePosition) {
        if (position === TooltipPosition.top || position === TooltipPosition.bottom) {
          if (coords.top < 0) {
            // Move to bottom
            correctedPosition = TooltipPosition.bottom;
          } else if (coords.top + tooltipRect.height > window.innerHeight) {
            // Move to top
            correctedPosition = TooltipPosition.top;
          }

          if (coords.left < 0) {
            correctedAlignment = TooltipAlignment.end;
          } else if (coords.left + tooltipRect.width > window.innerWidth) {
            correctedAlignment = TooltipAlignment.start;
          }
        } else {
          if (coords.left < 0) {
            // Move to right
            correctedPosition = TooltipPosition.right;
          } else if (coords.left + tooltipRect.width > window.innerWidth) {
            // Move to left
            correctedPosition = TooltipPosition.left;
          }

          if (coords.top < 0) {
            correctedAlignment = TooltipAlignment.end;
          } else if (coords.top + tooltipRect.height > window.innerHeight) {
            correctedAlignment = TooltipAlignment.start;
          }
        }
      }

      // Recalculate the position
      coords = calculatePosition(correctedPosition, correctedAlignment, childRect, tooltipRect);
      handleCoords = calculateHandlePosition(correctedPosition, correctedAlignment);
      const handlePosition = calculateHandleShape(correctedPosition, light);

      // Set the position
      tooltipRef.current.style.top = `${coords.top}px`;
      tooltipRef.current.style.left = `${coords.left}px`;

      // Set the handle position
      handleRef.current.style.top = handleCoords.top;
      handleRef.current.style.left = handleCoords.left;
      handleRef.current.style.borderLeft = handlePosition.borderLeft;
      handleRef.current.style.borderRight = handlePosition.borderRight;
      handleRef.current.style.borderTop = handlePosition.borderTop;
      handleRef.current.style.borderBottom = handlePosition.borderBottom;
      handleRef.current.className = handlePosition.borderColor;
    };
    const handleMouseLeave = () => {
      tooltipRef.current.style.display = "none";
    };

    childrenRef.current.forEach((child) => {
      child.addEventListener("mouseenter", handleMouseEnter);
      child.addEventListener("mouseleave", handleMouseLeave);
    });

    return () => {
      childrenRef.current.forEach((child) => {
        child?.removeEventListener("mouseenter", handleMouseEnter);
        child?.removeEventListener("mouseleave", handleMouseLeave);
      });
    };
  }, [children]);

  // If more than one child, warn
  if (React.Children.count(children) > 1) {
    console.warn("Tooltip component should only have one child. More than one child will cause undefined behaviour.");
  }

  return (
    <>
      {/** Allows us to get ref without affecting the DOM structure */}
      {/* @ts-expect-error Type issues with cloneElement and ReactNode allowing strings */}
      {React.Children.map(children, (child, index) => React.cloneElement(child, {
        // eslint-disable-next-line no-return-assign
        ref: (ref) => (childrenRef.current[index] = ref),
      }))}
      <div
        ref={tooltipRef}
        className={`select-none typography-medium-base p-2 rounded-sm shadow-sm 
          ${light ? "bg-bg-subtle text-text-base" : "bg-bg-inverted text-text-on-color"}`}
        style={{
          position: "absolute",
          display: "none",
          zIndex: zIndex.tooltip,
        }}
      >
        <div
          ref={handleRef}
          style={{
            width: 0,
            height: 0,
            position: "absolute",
          }}
        />
        {content}
      </div>
    </>
  );
};

export default Tooltip;
